import cashierActions from 'features/cashier/services/actions';
import cashierSelectors from 'features/cashier/services/selectors';
import { IStampLoyaltyReward, LOYALTY_STAMPS_REWARD_TYPE } from 'features/cashier/services/types/stamp-loyalty';
import { ChevronRight } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import shopSelectors from 'services/shop/selectors';
import { useAppDispatch } from 'store/hooks';
import ModalExistDiscount, { ModalExistDiscountRefs } from '../../Discount/ModalExistDiscount';
import styled from 'styled-components';
import DetailLoyaltyStamp from './DetailLoyaltyStamp';
import { formatRewardLabel } from './format-reward-label';

/**
 * Thanh tiến độ stamp loyalty của customer đang chọn.
 * Ẩn hoàn toàn khi shop tắt chương trình, khi khách đã đạt hết mọi mốc thưởng,
 * hoặc khi ticket chưa gắn customer nào.
 */
const StampReward = () => {
  const dispatch = useAppDispatch();
  const data = cashierSelectors.getStampLoyaltyInfo();
  const customerId = cashierSelectors.getSelectedCustomer()?.customerId;
  const autoOpen = cashierSelectors.getAutoOpenStampRewards();
  const [visibleDetail, setVisibleDetail] = useState(false);
  const categories = shopSelectors.data.categories();
  const discountTicket = cashierSelectors.getDiscountTicket();
  const existDiscountRef = useRef<ModalExistDiscountRefs>(null);

  // Cờ chỉ bật ở action đổi khách, nên mở ticket có sẵn khách sẽ không bung modal.
  // Reducer tự tắt cờ khi khách không có phần thưởng nào đang mở khoá.
  useEffect(() => {
    if (!autoOpen || !data?.enabled || !data.availableRewards?.length) return;
    dispatch(cashierActions.clearAutoOpenStampRewards());
    setVisibleDetail(true);
  }, [autoOpen, data]);

  // Khách đạt hết mọi mốc thì nextReward về null, nhưng phần thưởng chưa dùng vẫn phải thấy được
  const shownReward = data?.nextReward || data?.availableRewards?.[0];
  // ticket chua gan khach thi khong co stamp nao de hien
  if (!customerId || !data?.enabled || !shownReward) return null;

  // BE trả totalStamp kiểu số thực, làm tròn xuống để không hứa nhiều hơn thực tế
  const currentStamp = Math.floor(data.totalStamp || 0);
  // Phần thưởng đang chờ dùng. Khi khách đã đạt hết mốc thì nó chính là shownReward,
  // lúc đó không cần nhắc lại bằng pill nữa.
  const pendingReward = data.nextReward ? data.availableRewards?.[0] : null;

  const handleApplyReward = (reward: IStampLoyaltyReward) => {
    setVisibleDetail(false);

    if (reward.rewardType === LOYALTY_STAMPS_REWARD_TYPE.FREE_SERVICE) {
      const service = categories
        .flatMap(category => category.services || [])
        .find(o => o.id === reward.serviceId);
      dispatch(cashierActions.applyStampReward({ reward, service }));
      return;
    }

    // hai kiểu giảm giá đều ghi vào ô discount ticket, phải hỏi trước khi đè cái đang có
    const apply = () => dispatch(cashierActions.applyStampReward({ reward }));
    if (!discountTicket?.discount) {
      apply();
      return;
    }
    existDiscountRef.current?.open(apply);
  };

  const handleCancelReward = () => {
    setVisibleDetail(false);
    dispatch(cashierActions.cancelStampReward());
  };

  return (
    <>
      <StampRewardStyled type='button' onClick={() => setVisibleDetail(true)}>
        <StarBadge />
        <div className='stamp-group'>
          <span className='label'>Stamps loyalty</span>
          <span className='value'>{currentStamp}/{data.nextReward?.stampsRequired || data.totalSlots}</span>
        </div>
        <span className='divider' />

        {!!pendingReward && (
          <>
            <div className='available-group'>
              <span className='label'>Available reward</span>
              <span className='value'>{formatRewardLabel(pendingReward)}</span>
            </div>
            <span className='divider' />
          </>
        )}

        <div className='reward-group'>
          <span className='label'>{data.nextReward ? 'Next reward' : 'Reward ready'}</span>
          <span className='value'>{formatRewardLabel(shownReward)}</span>
        </div>
        <ChevronRight size={22} color='#1D2129' />
      </StampRewardStyled>
      <DetailLoyaltyStamp
        visible={visibleDetail}
        data={data}
        onClose={() => setVisibleDetail(false)}
        onApply={handleApplyReward}
        onCancelReward={handleCancelReward}
      />
      <ModalExistDiscount ref={existDiscountRef} target='ticket' />
    </>
  );
};

export default StampReward;

const StampRewardStyled = styled.button`
  display: flex;
  align-items: center;
  gap: 14px;
  align-self: stretch;
  width: calc(100% - 32px);
  min-height: 58px;
  margin: 0 16px 0.5rem;
  padding: 6px 14px;
  border-radius: 16px;
  background: #F3D157;
  font-family: Poppins;
  text-align: left;

  .stamp-group, .available-group, .reward-group {
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .label {
    font-size: 1rem;
    font-weight: 400;
    line-height: 1.25;
    color: #3D3D3D;
    white-space: nowrap;
  }

  .value {
    font-size: 1.4rem;
    font-weight: 800;
    line-height: 1.25;
    color: #1D2129;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .stamp-group {
    flex: 0 0 auto;
  }

  /* Phần thưởng dùng được ngay. Nền xanh để tách hẳn khỏi nền vàng của thanh */
  .available-group {
    flex: 0 0 auto;
    align-items: center;
    padding: 4px 20px;
    border-radius: 12px;
    background: #DDF5E0;

    .label, .value {
      color: #0C6B3D;
    }
  }

  .reward-group {
    flex: 1;
    min-width: 0;
    align-items: flex-end;
    text-align: right;

    .value {
      font-size: 1.3rem;
    }
  }

  .divider {
    flex-shrink: 0;
    width: 1px;
    align-self: stretch;
    margin: 6px 0;
    background: rgba(0, 0, 0, 0.16);
  }
`;

const StarBadge = () => (
  <svg width='32' height='32' viewBox='0 0 32 32' fill='none' xmlns='http://www.w3.org/2000/svg' style={{ flexShrink: 0, zoom: 1.5 }}>
    <circle cx='16' cy='16' r='16' fill='#C84B31' />
    <path d='M16 7.5L18.6 13.1L24.5 13.9L20.2 18.2L21.3 24.5L16 21.5L10.7 24.5L11.8 18.2L7.5 13.9L13.4 13.1L16 7.5Z' fill='#FFFFFF' />
  </svg>
);
